"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Mail, Phone, MapPin } from "lucide-react";
export default function ContactUs() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  return <section id="contact-us" ref={sectionRef} className="bg-light-background py-16 md:py-24">
      <div className="container mx-auto px-6 lg:px-12">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 transition-all duration-[var(--duration-fast)] ease-[var(--ease-out-back)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {}
          <div className="order-2 lg:order-1">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground-light mb-4">
              Get in Touch
            </h2>
            <p className="text-muted mb-8">
              We'd love to hear from you. Fill out the form below and we'll get back to you as soon as possible.
            </p>

            <FormProvider className="space-y-6" formId="contact-form-hero" sectionName="contact-us" defaultValues={{
            name: "",
            email: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out! We'll get back to you soon.">
              <ContactForm />
            </FormProvider>
          </div>

          {}
        {/*
                  <div className="order-1 lg:order-2 space-y-8">
                    <div className="space-y-6">
                       <h3 className="font-display text-2xl font-semibold text-foreground-light mb-4">
                        Contact Information
                      </h3>
                       <div className="flex items-start gap-4">
                        <div className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                          <Mail className="size-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground-light mb-1">Email</p>
                          <p className="text-muted">info@coastalai.tech</p>
                        </div>
                      </div>
                       <div className="flex items-start gap-4">
                        <div className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                          <Phone className="size-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground-light mb-1">Phone</p>
                          <p className="text-muted">To be determined</p>
                        </div>
                      </div>
                       <div className="flex items-start gap-4">
                        <div className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                          <MapPin className="size-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground-light mb-1">Address</p>
                          <p className="text-muted">To be determined</p>
                        </div>
                      </div>
                    </div>
           
                    {}
                    <div className="overflow-hidden rounded-lg shadow-medium transition-transform duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:scale-105">
            
                    </div>  
                  </div>
               */} 
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-accent-light border border-accent/20 rounded-lg shadow-soft">
        <p className="text-foreground-light font-medium mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      },
      maxLength: {
        value: 50,
        message: "Name must be less than 50 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="Your Name" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your project..." rows={5} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-primary shadow-xs h-9 px-4 py-2 has-[>svg]:px-3 w-full !text-[hsl(210,20%,98%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}